import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtUSD, fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge, statusTone } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { Loader, KpiCard } from "../components/KpiCard";
import { Modal, Field, TextInput, SelectInput, AddButton } from "../components/Form";
import { Award } from "lucide-react";

const STATUS = ["Researching", "Submitted", "Awarded", "Active", "Reporting", "Closed"];
const GTYPES = ["Foundation", "Government", "Corporate", "Individual"];

export default function Grants() {
  const { user } = useAuth();
  const canManage = ["admin", "finance_officer"].includes(user?.role);
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ grant_name: "", funding_organization: "", grant_type: "Foundation", status: "Active", award_amount: "", amount_received: "", restricted_purpose: "", grant_manager: "", reporting_frequency: "Quarterly", next_report_due: "" });

  const load = () => api.get("/grants").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/grants", { ...form, award_amount: Number(form.award_amount || 0), amount_received: Number(form.amount_received || 0), next_report_due: form.next_report_due || null });
      toast.success("Grant added");
      setOpen(false);
      setForm({ ...form, grant_name: "", funding_organization: "", award_amount: "", amount_received: "" });
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/grants/${id}`, { status }); toast.success("Status updated"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (!rows) return <Loader />;
  const totalAward = rows.reduce((s, r) => s + r.award_amount, 0);
  const received = rows.reduce((s, r) => s + r.amount_received, 0);

  const columns = [
    { key: "grant_name", label: "Grant", className: "font-medium" },
    { key: "funding_organization", label: "Funder", className: "text-[#5C5C54]" },
    { key: "grant_type", label: "Type", className: "text-[#5C5C54]" },
    { key: "award_amount", label: "Award", align: "right", render: (r) => fmtUSD(r.award_amount) },
    { key: "amount_received", label: "Received", align: "right", render: (r) => <span className="text-inflow">{fmtUSD(r.amount_received)}</span> },
    { key: "remaining_balance", label: "Remaining", align: "right", render: (r) => fmtUSD(r.remaining_balance) },
    { key: "next_report_due", label: "Report Due", render: (r) => fmtDate(r.next_report_due) },
    { key: "status", label: "Status", render: (r) => canManage
      ? <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} data-testid={`grant-status-${r.id}`}
          className="text-xs border border-stone-300 rounded px-2 py-1 bg-white">{STATUS.map((s) => <option key={s}>{s}</option>)}</select>
      : <StatusBadge tone={statusTone(r.status)}>{r.status}</StatusBadge> },
  ];

  return (
    <div>
      <PageHeader title="Grant Management" subtitle="Track the full grant lifecycle, awards, drawdowns and reporting deadlines."
        action={<AddButton onClick={() => setOpen(true)} label="New Grant" testid="add-grant-btn" />} />
      <div className="grid grid-cols-3 gap-6 mb-6">
        <KpiCard label="Total Awarded" value={fmtUSD(totalAward)} icon={Award} accent="#B45309" />
        <KpiCard label="Funds Received" value={fmtUSD(received)} accent="#2E7D32" />
        <KpiCard label="Remaining" value={fmtUSD(totalAward - received)} />
      </div>
      <DataTable columns={columns} rows={rows} testid="grants-table" />

      <Modal open={open} onOpenChange={setOpen} title="New Grant" onSubmit={submit} busy={busy} testid="grant-modal">
        <Field label="Grant Name" span={2}><TextInput required value={form.grant_name} onChange={(e) => setForm({ ...form, grant_name: e.target.value })} /></Field>
        <Field label="Funding Organization" span={2}><TextInput required value={form.funding_organization} onChange={(e) => setForm({ ...form, funding_organization: e.target.value })} /></Field>
        <Field label="Type"><SelectInput value={form.grant_type} onChange={(e) => setForm({ ...form, grant_type: e.target.value })} options={GTYPES} /></Field>
        <Field label="Status"><SelectInput value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} options={STATUS} /></Field>
        <Field label="Award Amount"><TextInput type="number" value={form.award_amount} onChange={(e) => setForm({ ...form, award_amount: e.target.value })} /></Field>
        <Field label="Amount Received"><TextInput type="number" value={form.amount_received} onChange={(e) => setForm({ ...form, amount_received: e.target.value })} /></Field>
        <Field label="Grant Manager"><TextInput value={form.grant_manager} onChange={(e) => setForm({ ...form, grant_manager: e.target.value })} /></Field>
        <Field label="Next Report Due"><TextInput type="date" value={form.next_report_due} onChange={(e) => setForm({ ...form, next_report_due: e.target.value })} /></Field>
        <Field label="Restricted Purpose" span={2}><TextInput value={form.restricted_purpose} onChange={(e) => setForm({ ...form, restricted_purpose: e.target.value })} /></Field>
      </Modal>
    </div>
  );
}
